import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { getAuthPortalLoginUrl } from 'utils/authPortal';
import { verifyAuthGuardSession } from 'utils/authGuardSession';
import { hasInsightSession } from 'utils/hasInsightSession';
import { isLoggingOut } from 'utils/logoutState';

// ==============================|| AUTH GUARD ||============================== //

function redirectToLogin() {
  window.location.replace(getAuthPortalLoginUrl());
}

/**
 * Authentication guard for routes. Redirects when session cookies are missing (e.g. after logout).
 * Also handles bfcache restore: "Back" after logout can show a stale SPA snapshot unless we re-check.
 *
 * After Change Email / Change Password, OC may return to the dashboard with leftover Insight
 * cookies. In that case only (visit flag set), we confirm OC is dead before forcing MUI login.
 * Normal browsing never probes auth/me.
 *
 * During an explicit logout every redirect here stands down (PRJCBUGEN-65938). Logout clears
 * `_Id`/`accountId` and then navigates to the OC logout URL; without this guard the render below
 * fires `replace(loginUrl)` in between, that replace wins because it is issued last, and OC — whose
 * SSO cookies are HttpOnly and were never cleared — signs the user straight back in.
 */
export default function AuthGuard({ children }) {
  const location = useLocation();

  useEffect(() => {
    if (isLoggingOut()) return;
    if (!hasInsightSession()) {
      redirectToLogin();
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;

    verifyAuthGuardSession()
      .then((result) => {
        if (!cancelled && result === 'login' && !isLoggingOut()) {
          redirectToLogin();
        }
      })
      .catch(() => {
        // Ignore probe failures; cookie-only AuthGuard still protects routes.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted && !isLoggingOut() && !hasInsightSession()) {
        redirectToLogin();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  if (!hasInsightSession()) {
    // Render-phase redirect. Suppressed during logout so it cannot override the logout navigation.
    if (!isLoggingOut()) {
      redirectToLogin();
    }
    return null;
  }

  return children;
}

AuthGuard.propTypes = { children: PropTypes.any };
